import { useEffect, useState } from 'react'
import { useHeadlessCheckoutContext } from '@/context/HeadlessCheckoutContext'
import { getSelectedVariant } from '@/utils/getSelectedVariant'
import LoadingState from '@/components/LoadingState'

import classes from './ProductForm.module.scss'
import { getLocalStorageItem } from '@/utils/localStorageHelper'

const defaultFormFields = {
  recipient_name: '',
  recipient_email: '',
  gift_message: '',
}

const ProductForm = ({
  giftOptionChecked,
  handle,
  product,
  variants,
  selectedVariant,
  setSelectedVariant,
}) => {
  const { addItemToOrder, data, updateGiftOptionMetadata, allSubsInCartAreGiftable } =
    useHeadlessCheckoutContext()
  const [giftFormFields, setFormFields] = useState(defaultFormFields)
  const [allowAddon, setAllowAddon] = useState(false)
  const isAddon = selectedVariant.sku?.includes('AO-')
  const isSwap = selectedVariant.sku?.includes('SWP-')
  const isSubscription = product.tags?.includes('Subscription Box')
  const [selectedOptions, setSelectedOptions] = useState(
    selectedVariant.content?.selectedOptions,
  )
  const [isLoading, setIsLoading] = useState(false)
  const [quantity, setQuantity] = useState(1)
  const [buttonText, setButtonText] = useState('')

  let options = null
  if (product?.content?.options?.some((option) => option.values.length > 1)) {
    options = product?.content?.options
  }

  useEffect(() => {
    // This cannot be done outside of the useEffect because it will try to determine the value on build, not on page load
    function grabAllowAddonValue() {
      setAllowAddon(new Boolean(getLocalStorageItem('allowAddon')).valueOf())
    }

    // Doing this here so that we can get updates when the addon value changes while being set in the _app.js file
    if (typeof window !== 'undefined') {
      window.addEventListener('storage', grabAllowAddonValue)
    }

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('storage', grabAllowAddonValue)
      }
    }
  }, [])

  useEffect(() => {
    setButtonText(
      selectedVariant
        ? (isAddon && !allowAddon) || isSwap
          ? 'Manage Your Plan to Add'
          : selectedVariant.availableForSale
            ? 'Add To Cart'
            : 'Sold Out'
        : 'Select Option',
    )
  }, [selectedVariant, isAddon, allowAddon, isSwap])

  useEffect(() => {
    const newFormFields = { ...giftFormFields }
    const recipient_name = data?.cart?.properties?.recipient_name
    const recipient_email = data?.cart?.properties?.recipient_email
    const gift_message = data?.cart?.properties?.gift_message
    if (recipient_name) newFormFields.recipient_name = recipient_name
    if (recipient_email) newFormFields.recipient_email = recipient_email
    if (gift_message) newFormFields.gift_message = gift_message
    setFormFields(newFormFields)
  }, [data?.cart?.properties])

  const handleGiftInfo = (event) => {
    const { name, value } = event.target
    setFormFields({ ...giftFormFields, [name]: value })
  }

  const handleOptionChange = (event, option) => {
    const newOption = { name: option.name, value: event.target.value }
    const optionIndex = selectedOptions.findIndex((selectedOption) => {
      return selectedOption.name === newOption.name
    })

    const newSelectedOptions = [...selectedOptions]
    if (optionIndex > -1) {
      newSelectedOptions.splice(optionIndex, 1, newOption)
      setSelectedOptions([...newSelectedOptions])
    } else {
      setSelectedOptions([...newSelectedOptions, newOption])
    }
    const variant = getSelectedVariant({
      variants,
      options: newSelectedOptions,
    })
    setSelectedVariant(variant ? { ...variant } : null)
  }

  const handleQuantityChange = (event) => {
    setQuantity(+event.target.value)
  }

  const handleAddItem = async (event) => {
    event.preventDefault()
    setIsLoading(true)

    const frequency = selectedVariant.content?.selectedOptions.find(
      (option) => option.name === 'frequency',
    )

    let subscriptionProperties = {}

    if (product.tags?.includes('Subscription Box')) {
      // TODO: add some logic here to determine if regular or prepaid
      subscriptionProperties = {
        interval_text: frequency.value,
        membership_type: 'regular',
      }
    }

    await addItemToOrder({
      variant: selectedVariant,
      quantity,
      properties: subscriptionProperties,
      product,
      redirectToCart: true,
    })
    if (giftOptionChecked) {
      await updateGiftOptionMetadata(giftFormFields)
    }
    setIsLoading(false)
  }

  return (
    <form
      onSubmit={(event) => handleAddItem(event)}
      className={classes['gift__info']}
    >
      <div className={classes['quantity']}>
        <label htmlFor={`quantity-${product.sourceEntryId}`}>Quantity:</label>
        <input
          id={`quantity-${product.sourceEntryId}`}
          type="number"
          min="1"
          value={quantity}
          onChange={handleQuantityChange}
        />
      </div>

      {!isSubscription && options && options.length > 0 && (
        <div className={classes['gift-card']}>
          {options.map((option) => (
            <>
              <div className={classes['gift-card__amount']}>
                <h4 className="heading__pdp-label">{option.name}</h4>
              </div>

              <div className={classes['gift-card__buttons']}>
                {/* VARIANT OPTIONS */}
                {option.values.map((value, vIndex) => (
                  <div key={vIndex} className={classes['btn']}>
                    <input
                      type="radio"
                      id={value}
                      name="giftCardButtons"
                      onChange={($event) => handleOptionChange($event, option)}
                      value={value}
                      checked={
                        selectedVariant.content?.selectedOptions[0].value ===
                        value
                      }
                    />
                    <label className="h5" htmlFor={value}>
                      {option.name === 'Amount' ? '$' : ''}
                      {value}
                    </label>
                  </div>
                ))}
              </div>
            </>
          ))}
        </div>
      )}

      <button
        type="submit"
        disabled={
          !selectedVariant.availableForSale ||
          selectedVariant.quantity === 0 ||
          (isAddon && !allowAddon) ||
          isSwap ||
          isLoading ||
          !allSubsInCartAreGiftable
        }
        className="btn salmon"
      >
        {isLoading ? <LoadingState /> : buttonText}
      </button>
      {giftOptionChecked && allSubsInCartAreGiftable && (
        <p className={classes['tooltiptext']}> <br />By adding a gift to your cart, it will turn everything in your cart into a gift order.</p>
      )}
      {giftOptionChecked && !allSubsInCartAreGiftable && <p className={classes['tooltiptext']}> <br />You have a non-gift subscription in your cart. Please remove it before adding a gift.</p>}
    </form>
  )
}

export default ProductForm
